import React, { useImperativeHandle, useRef, useState } from 'react';

import {
  Flex,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  forwardRef
} from '@chakra-ui/react';
import { Badge, ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { getFormattedDate } from 'utils/utils';
import { useEventReviewCount } from 'clients/internal/platformGateway';
import { ReviewingEvent } from 'typings/event';

const ITEMS_PER_PAGE = 5; // Number of events to show per page in Review Tasks Table

const getPaginatedEvents = (
  events: ReviewingEvent[],
  pageNo: number,
  pageSize: number
) => {
  const start = pageSize * (pageNo - 1);
  const end = pageSize * pageNo;

  return events.slice(start, end);
};

interface Props {
  setPendingReviewCount: (count: number) => void;
  reviewingEvents: any[];
  isLoading?: boolean;
}

interface ReviewTasksHandle {
  scrollToReviewTasks: () => void;
}

const ReviewTableThText = ({ children }: { children: string }) => (
  <Text
    textStyle="headingItem"
    textTransform="capitalize"
    textColor="neutralPrimaryContent"
    fontWeight={500}
    letterSpacing={0}
  >
    {children}
  </Text>
);

const ReviewTasks: React.FC<Props & React.RefAttributes<ReviewTasksHandle>> =
  forwardRef((props, ref) => {
    const reviewTasksRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollToReviewTasks() {
        if (reviewTasksRef.current) {
          reviewTasksRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }));

    const { reviewingEvents = [], isLoading, setPendingReviewCount } = props;

    // state
    const [currentPage, setCurrentPage] = useState(1);

    const { data: reviewCounts } = useEventReviewCount();

    const eventsWithReviewCounts = Object.keys(reviewCounts).map(eventId => {
      const event = reviewingEvents.find(ev => ev.id === eventId) || {};

      return {
        ...event,
        id: eventId,
        name: event.name || 'Unnamed Event', // Fallback if the event name is not available
        end_date: event.end_date || '', // Fallback if the end date is not available
        abstractReviewCount: reviewCounts[eventId]?.abstractReviewCount || 0,
        paperReviewCount: reviewCounts[eventId]?.paperReviewCount || 0
      };
    });

    const totalReviewCounts = eventsWithReviewCounts.reduce(
      (acc, event) =>
        acc +
        (event.abstractReviewCount as number) +
        (event.paperReviewCount as number),
      0
    );

    setPendingReviewCount(totalReviewCounts);

    const totalPages = Math.ceil(
      eventsWithReviewCounts.length / ITEMS_PER_PAGE
    );
    const isLastPage = currentPage === totalPages;
    const isFirstPage = currentPage === 1;

    const paginatedEvents =
      totalPages === 1
        ? eventsWithReviewCounts
        : getPaginatedEvents(
            eventsWithReviewCounts,
            currentPage,
            ITEMS_PER_PAGE
          );

    const renderSkeleton = () => {
      return isLoading
        ? Array.from({ length: ITEMS_PER_PAGE }).map((_value, index) => (
            <Tr key={index}>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
              <Td>
                <Skeleton height="30px" />
              </Td>
            </Tr>
          ))
        : null;
    };

    const nextPage = () => {
      if (isLastPage) {
        return;
      }
      setCurrentPage(currentPage + 1);
    };

    const prevPage = () => {
      if (isFirstPage) {
        return;
      }
      setCurrentPage(currentPage - 1);
    };

    const handleReviewEvent = (eventId: string, type: 'ABSTRACT' | 'PAPER') => {
      switch (type) {
        case 'ABSTRACT':
          window.open(`/conference/event/${eventId}/abm/review`, '_blank');
          break;
        case 'PAPER':
          window.open(`/conference/event/${eventId}/prc/review`, '_blank');
          break;
        default:
          window.open(`/conference/event/${eventId}`, '_blank');
          break;
      }
    };

    if (totalReviewCounts === 0) {
      return <></>;
    }

    return (
      <div ref={reviewTasksRef}>
        <Flex justifyContent="space-between" marginBottom="8">
          <Text textStyle="headingSectionBrand">Review Tasks</Text>
        </Flex>
        <TableContainer width={{ base: 'full' }} mx="auto" marginBottom="8">
          <Table variant="simple">
            <Thead backgroundColor="surfaceTertiaryFill">
              <Tr>
                <Th width="31%">
                  <ReviewTableThText>Event</ReviewTableThText>
                </Th>
                <Th width="31%">
                  <ReviewTableThText>Submissions to Review</ReviewTableThText>
                </Th>
                <Th width="31%">
                  <ReviewTableThText>Deadline</ReviewTableThText>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody backgroundColor="surfacePrimaryFill">
              {renderSkeleton()}

              {paginatedEvents.map(event => {
                const rEventId = event?.id;
                const abstractCount = reviewCounts[rEventId]
                  ?.abstractReviewCount as number;
                const paperCount = reviewCounts[rEventId]
                  ?.paperReviewCount as number;

                const abstractText =
                  abstractCount +
                  (abstractCount === 1 ? ` Abstract` : ` Abstracts`);

                const paperText =
                  paperCount + (paperCount === 1 ? ` Paper` : ` Papers`);
                return (
                  <Tr
                    key={rEventId}
                    borderBottom="1px"
                    borderColor="neutralPrimaryBorder"
                  >
                    <Td
                      maxWidth="426px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      <Tooltip label={event.name} placement="top" hasArrow>
                        {event.name || ''}
                      </Tooltip>
                    </Td>
                    <Td>
                      <Flex gap="0.5rem">
                        {!!abstractCount && (
                          <Badge variant="neutral" size="context">
                            {abstractText}
                          </Badge>
                        )}
                        {!!paperCount && (
                          <Badge variant="neutral" size="context">
                            {paperText}
                          </Badge>
                        )}
                      </Flex>
                    </Td>
                    <Td>
                      {event.end_date
                        ? getFormattedDate(new Date(event.end_date))
                        : '-'}
                    </Td>
                    <Td width="81px" textAlign="end">
                      <ButtonText
                        onClick={() =>
                          handleReviewEvent(
                            event.id,
                            abstractCount ? 'ABSTRACT' : 'PAPER'
                          )
                        }
                      >
                        Review
                      </ButtonText>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Flex justifyContent="end" marginBottom="8" gap="4">
            <ButtonText
              iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={prevPage}
              disabled={isFirstPage}
            >
              Prev
            </ButtonText>
            <ButtonText
              iconRight={<FontAwesomeIcon icon={faArrowRight} />}
              onClick={nextPage}
              disabled={isLastPage}
            >
              Next
            </ButtonText>
          </Flex>
        )}
      </div>
    );
  });

export default ReviewTasks;
